/* ==========================================================================
   Homepage motion (sections/homepage-template.liquid)

   1. Intro: as the image scrolls into view it settles in, its rounded edges
      easing out from slightly inset while the photo eases from a slight
      zoom, then drifts gently with the scroll. The title, paragraph and
      button beside it rise in one after another.
   2. "Explore our streaming collections": the cards rise in one after
      another. Cards that come into view together (a row, or the whole set
      on a wide screen) animate as one staggered batch, left to right; on
      narrower screens each row plays as it arrives.

   Both play once. They start as soon as the page is parsed (GSAP loads in
   the head), never wait for images or other scripts, and don't depend on
   ScrollTrigger: an IntersectionObserver decides when, GSAP does the
   motion. Replaces the old load-gated ScrollTrigger batch in application.js
   that hid the cards after the page had loaded and replayed on scroll back.

   Elements start hidden only while <html> has .reveal-ready (set in the
   head by page-transitions.js, which skips it for reduced motion and
   back/forward visits). Without that class, or without GSAP, nothing is
   hidden and nothing here runs.
   ========================================================================== */

(function () {
  'use strict';

  var root = document.documentElement;
  if (!root.classList.contains('reveal-ready')) return;

  var frames = Array.prototype.slice.call(document.querySelectorAll('.intro-image-frame'));
  var cards = Array.prototype.slice.call(document.querySelectorAll('.explore-section ul li'));

  function showAll() {
    var texts = Array.prototype.slice.call(document.querySelectorAll('.welcome-section .text-section > *'));
    frames.concat(cards, texts).forEach(function (el) { el.classList.add('is-revealed'); });
  }

  if (typeof gsap === 'undefined' || !('IntersectionObserver' in window)) {
    showAll();
    return;
  }

  // ---- 1. Intro: image and text --------------------------------------------

  // Image: settles in from slightly inset, its rounded edges easing out to
  // full size while the photo eases from a slight zoom. The clip is built
  // from a plain number each frame (inset, in %), rather than tweening the
  // clip-path string: browsers rewrite that string in a shorter form, which
  // pairs up the wrong values mid-tween.
  function clipAt(inset) {
    return 'inset(' + inset + '% ' + inset + '% ' + inset + '% ' + inset + '% round 15px)';
  }

  function revealFrame(frame) {
    var img = frame.querySelector('img');
    var state = { inset: 7 };
    // Hand the clip over from the stylesheet to an inline style, then tween
    frame.style.clipPath = clipAt(state.inset);
    frame.classList.add('is-revealed');
    gsap.to(state, {
      inset: 0,
      duration: 1.4,
      ease: 'power3.out',
      onUpdate: function () { frame.style.clipPath = clipAt(state.inset); },
      onComplete: function () { frame.style.clipPath = ''; }
    });
    gsap.fromTo(frame, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.8, ease: 'power1.out' });
    if (img) {
      // Settles to 1.08, leaving room for the scroll drift below
      gsap.fromTo(img, { scale: 1.18 }, { scale: 1.08, duration: 1.6, ease: 'power3.out' });
    }
  }

  // Watches the frame's parent: the browser measures visibility after the
  // clip, so the clipped frame itself wouldn't count as in view.
  var frameObserver = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      frameObserver.unobserve(entry.target);
      revealFrame(entry.target.querySelector('.intro-image-frame'));
    });
  }, { threshold: 0.2 });

  frames.forEach(function (frame) { frameObserver.observe(frame.parentElement); });

  // Gentle parallax while it's on screen: the photo drifts a few percent
  // inside its frame as the page scrolls. Needs ScrollTrigger (loaded at the
  // end of the page); without it the photo simply sits still.
  if (window.ScrollTrigger) {
    gsap.registerPlugin(ScrollTrigger);
    frames.forEach(function (frame) {
      var img = frame.querySelector('img');
      if (!img) return;
      gsap.fromTo(img, { yPercent: -3 }, {
        yPercent: 3,
        ease: 'none',
        scrollTrigger: { trigger: frame, start: 'top bottom', end: 'bottom top', scrub: true }
      });
    });
  }

  // Text: title, paragraph and button rise in one after another.
  var introTexts = Array.prototype.slice.call(document.querySelectorAll('.welcome-section .text-section'));

  var textObserver = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      textObserver.unobserve(entry.target);
      var parts = Array.prototype.slice.call(entry.target.children);
      parts.forEach(function (el) { el.classList.add('is-revealed'); });
      gsap.fromTo(parts, { autoAlpha: 0, y: 30 }, {
        autoAlpha: 1,
        y: 0,
        duration: 1,
        ease: 'power3.out',
        stagger: 0.12,
        clearProps: 'opacity,visibility,transform'
      });
    });
  }, { rootMargin: '0px 0px -12% 0px' });

  introTexts.forEach(function (text) { textObserver.observe(text); });

  // ---- 2. Explore cards --------------------------------------------------

  var pending = [];
  var queued = false;

  function flush() {
    queued = false;
    var batch = pending.sort(function (a, b) {
      return a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
    });
    pending = [];
    batch.forEach(function (card) { card.classList.add('is-revealed'); });
    gsap.fromTo(batch,
      { autoAlpha: 0, y: 40 },
      {
        autoAlpha: 1,
        y: 0,
        duration: 0.8,
        ease: 'power3.out',
        stagger: 0.1,
        overwrite: true,
        // Hand transforms back to the stylesheet (the cards' hover lift)
        clearProps: 'opacity,visibility,transform'
      });
  }

  var cardObserver = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      // Phones: the cards are a sideways row, so ones off to the right
      // would never scroll into view vertically. When the row arrives, they
      // all come in together.
      var group = window.innerWidth <= 767
        ? cards.filter(function (card) { return !card.classList.contains('is-revealed') && pending.indexOf(card) === -1; })
        : [entry.target];
      group.forEach(function (card) {
        cardObserver.unobserve(card);
        if (pending.indexOf(card) === -1) pending.push(card);
      });
    });
    if (pending.length && !queued) {
      queued = true;
      setTimeout(flush, 30);
    }
  }, { rootMargin: '0px 0px -10% 0px' });

  cards.forEach(function (card) { cardObserver.observe(card); });

  // A page restored from the back/forward cache keeps its DOM: show it all
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) showAll();
  });
})();

// ---- Hero text on phones ----------------------------------------------------
//
// Desktop pins the hero (application.js). Phones don't: the hero text stays
// fixed on screen while the hero image scrolls up behind it and the next
// section slides up over it; once the hero has gone, the text switches off so
// it can't show through further down. The title loop keeps running
// throughout.
//
// The text is only held (html.hero-hold, application.css.liquid) once this is
// set up, so if it can't run the hero text simply scrolls with the page.
(function () {
  'use strict';

  if (!window.matchMedia('(max-width: 768px)').matches) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (typeof gsap === 'undefined' || !window.ScrollTrigger) return;

  var heroText = document.getElementById('video-content');
  var heroBlock = document.querySelector('.content-block-video_background');
  if (!heroText || !heroBlock) return;

  gsap.registerPlugin(ScrollTrigger);
  document.documentElement.classList.add('hero-hold');

  ScrollTrigger.create({
    trigger: heroBlock,
    start: 'bottom top',
    onEnter: function () { gsap.set(heroText, { autoAlpha: 0 }); },
    onLeaveBack: function () { gsap.set(heroText, { autoAlpha: 1 }); }
  });
})();
